import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Select,
  Form,
  Input,
  Spin,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PageTitle from "../../../_components/PageTitle";
import { participantActions } from "../../../_actions";
import { appNotification } from "../../../_components";

const Participant = () => {
  const { Option } = Select;
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const [confirmChangeStatus, setConfirmChangeStatus] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);

  const [form] = Form.useForm();
  const [id, setId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newEnabled, setNewEnabled] = useState(false);
  const [modalData, setModalData] = useState([]);

  const {
    createParticipant,
    getAllParticipants,
    editParticipant,
    viewParticipant,
  } = participantActions;

  const {
    participants,
    allParticipantsLoading,
    allParticipantsError,
    addParticipantLoading,
    viewParticipantLoading,
    editParticipantLoading,
  } = useSelector((state) => state.participantState);

  const confirmChangeStatusModal = (id) => {
    setConfirmChangeStatus(true);
    setId(id);
  };

  const handleStatusChange = () => {
    const participant = participants.find(
      (participant) => participant.id === id
    );
    const newEnabled = participant.enabled ? 0 : 1;
    const code = participant.code;
    const name = participant.name;

    const data = {
      name: name,
      code: code,
      enabled: newEnabled,
    };
    const onSuccessCallback = () => {
      dispatch(getAllParticipants());
      setConfirmChangeStatus(false);
    };
    dispatch(editParticipant(id, data, onSuccessCallback));
  };

  const handleOk = () => {
    setIsCreateModalVisible(false);
    setIsUpdateModalVisible(false);
    setConfirmChangeStatus(false);
  };

  const onFinish = (e) => {
    e.preventDefault();
    const participant = {
      name: newName.trim(),
      code: newCode.trim(),
    };
    const onSuccessCallback = () => {
      setNewName("");
      setNewCode("");
      setNewEnabled(false);
      setIsCreateModalVisible(false);
      dispatch(getAllParticipants());
    };

    dispatch(createParticipant(participant, onSuccessCallback));
  };

  const onFinishUpdate = (e) => {
    e.preventDefault();
    const participant = {
      name: name.trim(),
      code: code.trim(),
      enabled: enabled,
    };
    const onSuccessCallback = () => {
      setName("");
      setCode("");
      setEnabled(false);
      setIsUpdateModalVisible(false);
      dispatch(getAllParticipants());
    };

    dispatch(editParticipant(id, participant, onSuccessCallback));
  };

  const handleCancel = () => {
    setIsStatusModalVisible(false);
    setIsCreateModalVisible(false);
    setIsUpdateModalVisible(false);
    setConfirmChangeStatus(false);
  };

  const showModal = () => {
    setIsCreateModalVisible(true);
  };

  const handleUpdate = (id, record) => {
    const participant = participants.find(
      (participant) => participant.id === id
    );
    setId(id);
    setName(participant.name);
    setCode(participant.code);
    setEnabled(participant.enabled);
    setIsUpdateModalVisible(true);
    setModalData(record);
  };
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Status",
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled, record) => {
        const status = enabled ? "active" : "inactive";
        let color = status === "active" ? "green" : "red";

        return (
          <Space size="large">
            <Tag
              color={color}
              key={enabled}
              style={{
                width: "5rem",
                textAlign: "center",
                // height: "32px",
                padding: "0.2rem",
              }}
            >
              {status.toUpperCase()}
            </Tag>
            <Button
              type="primary"
              danger
              style={{
                width: "5rem",
                textAlign: "center",
                // height: "32px",
                padding: "0.2rem",
              }}
              onClick={() => confirmChangeStatusModal(record.id)}
            >
              {status === "active" ? "Deactivate" : "Activate"}
            </Button>
          </Space>
        );
      },
    },
    {
      title: "",
      render: (_, record) => {
        const id = record.id;
        return <Button onClick={() => handleUpdate(id, record)}>Update</Button>;
      },
    },
  ];

  useEffect(() => {
    if (allParticipantsError) {
      appNotification(
        allParticipantsError.errorMessage,
        "Unable to fetch participants",
        "error"
      );
    }
    if (!isMounted.current) {
      dispatch(getAllParticipants());
      isMounted.current = true;
    }
  });

  return (
    <div>
      <PageTitle>Participant Configuration</PageTitle>
      <div
        style={{
          textAlign: "right",
          marginBottom: "1rem",
        }}
      >
        <Button type="primary" onClick={showModal}>
          <PlusOutlined />
          Create Participant
        </Button>
      </div>
      <Modal
        title="Title"
        open={confirmChangeStatus}
        onOk={handleStatusChange}
        onCancel={handleCancel}
      >
        <p>Are you sure?</p>
      </Modal>

      <Modal
        title="Create Participant"
        visible={isCreateModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={onFinish}
            loading={addParticipantLoading}
          >
            Submit
          </Button>,
        ]}
      >
        <Form
          form={form}
          onFinish={onFinish}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 14 }}
          layout="horizontal"
          initialValues={{ size: "default" }}
          size={"default"}
        >
          <Form.Item label="Name">
            <Input
              name="name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter name"
            />
          </Form.Item>
          <Form.Item label="Code">
            <Input
              name="code"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="Enter code"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Update Participant"
        visible={isUpdateModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={onFinishUpdate}
            loading={addParticipantLoading}
          >
            Submit
          </Button>,
        ]}
      >
        <Form
          form={form}
          onFinish={onFinish}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 14 }}
          layout="horizontal"
          initialValues={{ size: "default" }}
          size={"default"}
        >
          <Form.Item label="Name">
            <Input
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name"
            />
          </Form.Item>
          <Form.Item label="Code">
            <Input
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter code"
            />
          </Form.Item>
          <Form.Item label="Status">
            <Select
              name="status"
              value={enabled}
              onChange={(e) => setEnabled(e)}
              placeholder="Select status"
            >
              <Option value={1}>Active</Option>
              <Option value={0}>Inactive</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Table
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={participants}
        loading={
          allParticipantsLoading ||
          viewParticipantLoading ||
          editParticipantLoading
        }
        className="data__table"
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default Participant;
